import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { db, storage } from "../firebase";
import {
  collection, addDoc, onSnapshot, query, orderBy, serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";

const G = "#c9a55a";
const NAVY = "#12122a";
const DRIVE_PROXY_URL = process.env.REACT_APP_DRIVE_PROXY_URL;
const PAGE_SIZE = 12;

function Pagination({ page, total, onChange }) {
  const totalPages = Math.ceil(total / PAGE_SIZE);
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-2 mt-8">
      <button
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
        style={{ border: `1px solid ${G}40`, color: page === 1 ? "#4b4b6b" : G, minHeight: 44 }}
        className="px-5 py-2 rounded-xl text-sm disabled:cursor-not-allowed hover:opacity-70 transition-opacity">
        ← Prev
      </button>

      {/* Mobile: show "2 / 5" text. Desktop: show numbered buttons */}
      <span
        className="sm:hidden text-sm"
        style={{ color: `${G}90`, minWidth: 60, textAlign: "center" }}>
        {page} / {totalPages}
      </span>
      <div className="hidden sm:flex gap-1 flex-wrap justify-center">
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
          <button key={p} onClick={() => onChange(p)}
            style={{
              background: p === page ? `linear-gradient(135deg,${G},#a07838)` : "rgba(255,255,255,0.05)",
              border: `1px solid ${p === page ? G : `${G}25`}`,
              color: p === page ? "#fff" : "#6b6b8a",
              minWidth: 36,
              minHeight: 36,
            }}
            className="py-1 rounded-xl text-sm hover:opacity-80 transition-opacity">
            {p}
          </button>
        ))}
      </div>

      <button
        onClick={() => onChange(page + 1)}
        disabled={page === totalPages}
        style={{ border: `1px solid ${G}40`, color: page === totalPages ? "#4b4b6b" : G, minHeight: 44 }}
        className="px-5 py-2 rounded-xl text-sm disabled:cursor-not-allowed hover:opacity-70 transition-opacity">
        Next →
      </button>
    </div>
  );
}

function fetchJsonp(url) {
  return new Promise((resolve, reject) => {
    const cbName = `_gcb_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const script = document.createElement("script");
    window[cbName] = (data) => {
      delete window[cbName];
      document.head.removeChild(script);
      resolve(data);
    };
    script.src = `${url}?callback=${cbName}`;
    script.onerror = () => {
      delete window[cbName];
      document.head.removeChild(script);
      reject(new Error("Failed to load Drive photos"));
    };
    document.head.appendChild(script);
  });
}

const driveThumb = (id) => `https://drive.google.com/thumbnail?id=${id}&sz=w800`;
const driveFullUrl = (id) => `https://drive.google.com/file/d/${id}/view`;
const driveDownload = (id) => `https://drive.google.com/uc?export=download&id=${id}`;

async function downloadImage(url, filename) {
  try {
    const res = await fetch(url, { mode: "cors" });
    if (!res.ok) throw new Error("fetch failed");
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || "photo.jpg";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch {
    // CORS blocked — open download link in new tab
    window.open(url, "_blank");
  }
}

export default function BaptismGallery() {
  const [activeTab, setActiveTab] = useState("event");
  const [drivePage, setDrivePage] = useState(1);
  const [guestPage, setGuestPage] = useState(1);

  const switchTab = (tab) => {
    setActiveTab(tab);
    setDrivePage(1);
    setGuestPage(1);
  };

  const [drivePhotos, setDrivePhotos] = useState([]);
  const [loadingDrive, setLoadingDrive] = useState(true);
  const [driveError, setDriveError] = useState(null);

  const [guestPhotos, setGuestPhotos] = useState([]);

  // lightbox = { list: [{url,downloadUrl,fullUrl?,name,filename}], index: number }
  const [lightbox, setLightbox] = useState(null);
  const touch = useRef(null);
  const [imgKey, setImgKey] = useState(0); // forces fade on photo change

  const openLightbox = (list, index) => { setLightbox({ list, index }); setImgKey((k) => k + 1); };
  const closeLightbox = () => setLightbox(null);
  const navigate = (dir) => {
    setLightbox((lb) => {
      if (!lb) return null;
      const next = lb.index + dir;
      if (next < 0 || next >= lb.list.length) return lb;
      return { ...lb, index: next };
    });
    setImgKey((k) => k + 1);
  };

  const [uploaderName, setUploaderName] = useState(
    () => localStorage.getItem("baptism_guest_name") || ""
  );
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadDone, setUploadDone] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!document.getElementById("baptism-fonts")) {
      const link = document.createElement("link");
      link.id = "baptism-fonts";
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    if (!DRIVE_PROXY_URL) {
      setDriveError("no_key");
      setLoadingDrive(false);
      return;
    }
    fetchJsonp(DRIVE_PROXY_URL)
      .then((data) => {
        if (data.error) throw new Error(data.error);
        setDrivePhotos(data.files || []);
      })
      .catch((err) => setDriveError(err.message))
      .finally(() => setLoadingDrive(false));
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, "baptism_gallery_uploads"), orderBy("uploadedAt", "desc")),
      (snap) => setGuestPhotos(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, []);

  useEffect(() => {
    return () => { if (uploadPreview) URL.revokeObjectURL(uploadPreview); };
  }, [uploadPreview]);

  // Keyboard nav for desktop
  useEffect(() => {
    if (!lightbox) return;
    const handler = (e) => {
      if (e.key === "ArrowRight") navigate(1);
      else if (e.key === "ArrowLeft") navigate(-1);
      else if (e.key === "Escape") closeLightbox();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox]);

  // Touch: swipe left/right to navigate, swipe down to close
  const onTouchStart = (e) => {
    touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };
  const onTouchEnd = (e) => {
    if (!touch.current) return;
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    touch.current = null;
    if (Math.abs(dy) > Math.abs(dx)) {
      if (dy > 60) closeLightbox();
    } else {
      if (dx < -50) navigate(1);
      if (dx > 50) navigate(-1);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setUploadError("File is too large (max 10 MB).");
      return;
    }
    if (uploadPreview) URL.revokeObjectURL(uploadPreview);
    setUploadFile(file);
    setUploadPreview(URL.createObjectURL(file));
    setUploadDone(false);
    setUploadError("");
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploaderName.trim()) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadDone(false);
    setUploadError("");
    try {
      const ext = uploadFile.name.split(".").pop();
      const storageRef = ref(
        storage,
        `baptism_guest_photos/${Date.now()}_${uploaderName.trim().replace(/\s+/g, "_")}.${ext}`
      );
      const task = uploadBytesResumable(storageRef, uploadFile);
      await new Promise((resolve, reject) => {
        task.on("state_changed",
          (snap) => setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
          reject,
          resolve
        );
      });
      const url = await getDownloadURL(storageRef);
      await addDoc(collection(db, "baptism_gallery_uploads"), {
        url,
        uploaderName: uploaderName.trim(),
        fileName: uploadFile.name,
        uploadedAt: serverTimestamp(),
      });
      localStorage.setItem("baptism_guest_name", uploaderName.trim());
      setUploadFile(null);
      setUploadPreview(null);
      setUploadDone(true);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      console.error("Upload failed:", err);
      setUploadError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ background: "linear-gradient(160deg,#0d0d1e,#12122a,#0a0a18)", minHeight: "100vh", fontFamily: "'Cormorant Garamond',Georgia,serif" }}>
      <div style={{ height: 3, background: `linear-gradient(90deg,transparent,${G} 30%,${G} 70%,transparent)` }} />

      {/* Header */}
      <div className="max-w-4xl mx-auto px-4 pt-6 sm:pt-10 pb-4">
        <Link
          to="/baptism"
          style={{ color: `${G}60` }}
          className="inline-flex items-center text-sm hover:opacity-70 transition-opacity py-2">
          ← Back to Invite
        </Link>

        <div className="text-center mt-6 sm:mt-8 mb-8 sm:mb-10">
          <p style={{ letterSpacing: "0.2em", color: `${G}80` }} className="text-xs uppercase mb-3">
            Baptism of Munachimso Zoe Maxwell
          </p>
          <h1
            style={{ fontFamily: "'Playfair Display',serif", color: G, lineHeight: 1.1 }}
            className="text-4xl sm:text-6xl font-bold mb-3">
            Gallery
          </h1>
          <p style={{ color: "#6b6b8a", fontStyle: "italic" }} className="text-sm">
            Memories from a blessed day
          </p>
          <div className="flex items-center justify-center gap-3 max-w-xs mx-auto mt-5">
            <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg,transparent,${G}40)` }} />
            <span style={{ color: G }}>✦</span>
            <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg,${G}40,transparent)` }} />
          </div>
        </div>

        {/* ── EVENT PHOTOS (Google Drive) ── */}
        {activeTab === "event" && (
          <div className="pb-16">
            {loadingDrive && (
              <div className="text-center py-20">
                <div className="flex justify-center gap-2 mb-4">
                  {[0, 1, 2].map((i) => (
                    <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: G, animationDelay: `${i * 0.25}s` }} className="animate-bounce" />
                  ))}
                </div>
                <p style={{ color: "#6b6b8a", fontStyle: "italic" }} className="text-sm">Loading photos…</p>
              </div>
            )}

            {driveError === "no_key" && (
              <div style={{ border: `1px solid ${G}25`, background: "rgba(255,255,255,0.02)" }} className="rounded-2xl p-8 text-center">
                <p className="text-4xl mb-3">🔑</p>
                <p style={{ color: "#e2e8f0" }} className="text-base font-medium mb-2">Gallery proxy not configured</p>
                <p style={{ color: "#6b6b8a" }} className="text-sm">Add <code style={{ color: G }}>REACT_APP_DRIVE_PROXY_URL</code> to your .env file.</p>
              </div>
            )}

            {driveError && driveError !== "no_key" && (
              <div style={{ border: `1px solid rgba(248,113,113,0.25)`, background: "rgba(220,38,38,0.05)" }} className="rounded-2xl p-8 text-center">
                <p className="text-3xl mb-3">⚠️</p>
                <p style={{ color: "#fca5a5" }} className="text-sm">{driveError}</p>
              </div>
            )}

            {!loadingDrive && !driveError && drivePhotos.length === 0 && (
              <div style={{ border: `1px solid ${G}20`, background: "rgba(255,255,255,0.02)" }} className="rounded-2xl p-10 text-center">
                <p className="text-4xl mb-3">🌸</p>
                <p style={{ color: "#6b6b8a", fontStyle: "italic" }} className="text-sm">No photos yet — check back soon.</p>
              </div>
            )}

            {!loadingDrive && !driveError && drivePhotos.length > 0 && (
              <>
                <p style={{ color: `${G}60`, letterSpacing: "0.1em" }} className="text-xs uppercase text-center mb-4">
                  {drivePhotos.length} photo{drivePhotos.length !== 1 ? "s" : ""} · page {drivePage} of {Math.ceil(drivePhotos.length / PAGE_SIZE)}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                  {drivePhotos.slice((drivePage - 1) * PAGE_SIZE, drivePage * PAGE_SIZE).map((photo, localIdx) => (
                    <button
                      key={photo.id}
                      onClick={() => openLightbox(
                        drivePhotos.map((p) => ({ url: driveThumb(p.id), fullUrl: driveFullUrl(p.id), downloadUrl: driveDownload(p.id), name: p.name, filename: p.name })),
                        (drivePage - 1) * PAGE_SIZE + localIdx
                      )}
                      className="relative overflow-hidden rounded-xl sm:rounded-2xl group aspect-square active:opacity-80 transition-opacity"
                      style={{ border: `1px solid ${G}20` }}>
                      <img
                        src={driveThumb(photo.id)}
                        alt={photo.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div
                        className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ background: "rgba(0,0,0,0.25)" }}>
                        <span className="text-white text-2xl">🔍</span>
                      </div>
                    </button>
                  ))}
                </div>
                <Pagination page={drivePage} total={drivePhotos.length} onChange={(p) => { setDrivePage(p); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
              </>
            )}
          </div>
        )}

        {/* ── GUEST MEMORIES (Firebase Storage) ── */}
        {activeTab === "guests" && (
          <div className="space-y-6 pb-16">
            {guestPhotos.length === 0 ? (
              <div style={{ border: `1px solid ${G}20`, background: "rgba(255,255,255,0.02)" }} className="rounded-2xl p-10 text-center">
                <p className="text-4xl mb-3">🌸</p>
                <p style={{ color: "#6b6b8a", fontStyle: "italic" }} className="text-sm">No guest photos yet — be the first to share one!</p>
              </div>
            ) : (
              <>
                <p style={{ color: `${G}60`, letterSpacing: "0.1em" }} className="text-xs uppercase text-center">
                  {guestPhotos.length} memory{guestPhotos.length !== 1 ? "ies" : "y"} shared · page {guestPage} of {Math.ceil(guestPhotos.length / PAGE_SIZE)}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                  {guestPhotos.slice((guestPage - 1) * PAGE_SIZE, guestPage * PAGE_SIZE).map((photo, localIdx) => (
                    <button
                      key={photo.id}
                      onClick={() => openLightbox(
                        guestPhotos.map((p) => ({ url: p.url, downloadUrl: p.url, name: p.uploaderName, filename: `memory_${p.uploaderName}.jpg` })),
                        (guestPage - 1) * PAGE_SIZE + localIdx
                      )}
                      className="relative overflow-hidden rounded-xl sm:rounded-2xl group aspect-square active:opacity-80 transition-opacity"
                      style={{ border: `1px solid ${G}20` }}>
                      <img
                        src={photo.url}
                        alt={photo.uploaderName}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div
                        className="absolute bottom-0 left-0 right-0 px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ background: "linear-gradient(0deg,rgba(0,0,0,0.75),transparent)" }}>
                        <p style={{ color: "#f1f5f9", fontFamily: "'Cormorant Garamond',serif" }} className="text-sm truncate">
                          {photo.uploaderName}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
                <Pagination page={guestPage} total={guestPhotos.length} onChange={(p) => { setGuestPage(p); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
              </>
            )}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (() => {
        const photo = lightbox.list[lightbox.index];
        const total = lightbox.list.length;
        const hasPrev = lightbox.index > 0;
        const hasNext = lightbox.index < total - 1;
        return (
          <div
            className="fixed inset-0 z-50 flex flex-col items-center justify-center select-none"
            style={{ background: "rgba(0,0,0,0.96)", paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
            onClick={closeLightbox}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}>

            {/* Close */}
            <button
              onClick={closeLightbox}
              style={{ color: `${G}90` }}
              className="absolute top-4 right-4 w-11 h-11 flex items-center justify-center rounded-full text-3xl leading-none hover:opacity-70 transition-opacity z-10"
              aria-label="Close">
              ×
            </button>

            {/* Counter */}
            <p className="absolute top-5 left-1/2 -translate-x-1/2 text-xs" style={{ color: "#ffffff50" }}>
              {lightbox.index + 1} / {total}
            </p>

            {/* Swipe hint — only shown on touch devices */}
            <p className="sm:hidden absolute bottom-28 left-1/2 -translate-x-1/2 text-xs whitespace-nowrap" style={{ color: "#ffffff30" }}>
              ← swipe to browse · swipe down to close →
            </p>

            {/* Prev button (desktop) */}
            <button
              onClick={(e) => { e.stopPropagation(); navigate(-1); }}
              disabled={!hasPrev}
              style={{ color: hasPrev ? G : "#ffffff20" }}
              className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center rounded-full text-2xl hover:opacity-70 transition-opacity disabled:cursor-not-allowed"
              aria-label="Previous">
              ‹
            </button>

            {/* Next button (desktop) */}
            <button
              onClick={(e) => { e.stopPropagation(); navigate(1); }}
              disabled={!hasNext}
              style={{ color: hasNext ? G : "#ffffff20" }}
              className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 items-center justify-center rounded-full text-2xl hover:opacity-70 transition-opacity disabled:cursor-not-allowed"
              aria-label="Next">
              ›
            </button>

            {/* Image */}
            <div className="w-full px-4 sm:px-16 flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
              <img
                key={imgKey}
                src={photo.url}
                alt={photo.name}
                className="max-w-full rounded-xl sm:rounded-2xl shadow-2xl object-contain"
                style={{ maxHeight: "72dvh", animation: "lbFadeIn 0.18s ease" }}
              />
              <div className="mt-4 flex items-center gap-3 flex-wrap justify-center">
                {photo.name && (
                  <p style={{ color: `${G}80`, fontStyle: "italic" }} className="text-sm text-center">{photo.name}</p>
                )}
                <button
                  onClick={() => downloadImage(photo.downloadUrl, photo.filename)}
                  style={{ background: `linear-gradient(135deg,${G},#a07838)`, minHeight: 40 }}
                  className="flex items-center gap-2 text-white text-xs px-5 py-2 rounded-full hover:opacity-85 active:opacity-70 transition-opacity shadow-lg">
                  ↓ Download
                </button>
                {photo.fullUrl && (
                  <a
                    href={photo.fullUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: G, border: `1px solid ${G}50`, minHeight: 40 }}
                    className="flex items-center text-xs px-4 py-2 rounded-full hover:opacity-80 transition-opacity">
                    Open full size ↗
                  </a>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      <style>{`@keyframes lbFadeIn { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }`}</style>

      <div style={{ height: 3, background: `linear-gradient(90deg,transparent,${G} 30%,${G} 70%,transparent)` }} />
    </div>
  );
}
